/**
 * useHasRole Hook - RBAC check for protected routes
 *
 * PRIV-25.2: Implements role-based access control for Privacy KPI Dashboard
 *
 * RBAC Phase 4: Now primarily sources roles from AppState.orgUser.role_code
 * (populated by GET /lender/me). Falls back to Auth0 profile claims.
 *
 * @example
 * const hasAccess = useHasRole(['verascore_admin', 'auditor']);
 * if (!hasAccess) return <Unauthorized />;
 */

import { useMemo } from 'react';
import useAppState from '../common/AppState';

/**
 * Feature flag for Privacy KPI Dashboard
 * Set VITE_FEATURE_PRIVACY_KPI=true in Netlify env vars to enable
 */
export const FEATURE_PRIVACY_KPI_ENABLED = import.meta.env.VITE_FEATURE_PRIVACY_KPI === 'true';

/**
 * Admin roles that can access the Privacy KPI Dashboard
 * These correspond to Auth0 role claims in:
 * - profile.app_metadata.roles (array)
 * - OR JWT claim: https://members.verascore.com/roles
 *
 * Accepted roles:
 * - Platform Admin: Full platform administrator (Auth0 role name)
 * - verascore_admin: Legacy admin role
 * - auditor: Compliance/audit access
 * - ops: Operations team access
 */
export const ADMIN_ROLES = ['Platform Admin', 'verascore_admin', 'auditor', 'ops'] as const;
export type AdminRole = typeof ADMIN_ROLES[number];

/**
 * Check if user has any of the required roles
 * 
 * @param requiredRoles - Array of role strings to check against
 * @returns true if user has at least one of the required roles
 */
export function useHasRole(requiredRoles: string[]): boolean {
  const { profile, orgUser } = useAppState();

  return useMemo(() => {
    // No profile = not authenticated = no access
    if (!profile) {
      if (import.meta.env.DEV) console.log('[useHasRole] No profile, denying access');
      return false;
    }

    // RBAC Phase 4: Primary source is orgUser.role_code from GET /lender/me
    // This replaces TEMP_ADMIN_USER_IDS and direct Supabase role checks
    const roleSources: string[][] = [];

    // Source 1 (PRIMARY): RBAC role_code from /lender/me
    if (orgUser?.role_code) {
      // Map RBAC role codes to the legacy role names expected by callers
      // IMPORTANT: Only INTERNAL (VS_*) roles should map to admin-level roles.
      // FI roles (FI_ADMIN, LENDER_MANAGER, etc.) are EXTERNAL and must NOT
      // be granted access to internal admin pages.
      const rbacRoleMap: Record<string, string[]> = {
        'VS_SUPER_ADMIN': ['Platform Admin', 'verascore_admin', 'admin'],
        'VS_SUPPORT_ADMIN': ['Platform Admin', 'verascore_admin', 'admin'],
        'VS_COMPLIANCE': ['auditor', 'verascore_admin'],
        'VS_ENGINEERING': ['verascore_admin', 'ops'],
        'COMPLIANCE_AUDITOR': ['auditor'],
        'OPERATIONS_ANALYST': ['ops'],
        // FI roles intentionally NOT mapped to admin roles.
        // FI_ADMIN, LENDER_MANAGER, LOAN_OFFICER, etc. fall through
        // to the default: [role_code.toLowerCase()]
      };
      const mappedRoles = rbacRoleMap[orgUser.role_code] || [orgUser.role_code.toLowerCase()];
      roleSources.push(mappedRoles);
    }

    // Source 2 (FALLBACK): orgUser.role (legacy string from org_users table)
    if (orgUser?.role && !orgUser?.role_code) {
      const orgRoleMap: Record<string, string[]> = {
        admin: ['verascore_admin', 'admin'],
        auditor: ['auditor'],
        ops: ['ops'],
      };
      const mappedRoles = orgRoleMap[orgUser.role] || [orgUser.role];
      roleSources.push(mappedRoles);
    }

    // Source 3 (FALLBACK): app_metadata.roles (Auth0 custom claim)
    if (profile.app_metadata?.roles && Array.isArray(profile.app_metadata.roles)) {
      roleSources.push(profile.app_metadata.roles);
    }

    // Source 4 (FALLBACK): user_metadata.role (legacy single role)
    if (profile.user_metadata?.role) {
      roleSources.push([profile.user_metadata.role]);
    }

    // Flatten all role sources
    const userRoles = roleSources.flat();

    if (import.meta.env.DEV) console.log('[useHasRole] Checking roles:', {
      requiredRoles,
      userRoles,
      sources: {
        rbacRoleCode: orgUser?.role_code,
        orgUserRole: orgUser?.role,
        appMetadata: profile.app_metadata?.roles,
        userMetadata: profile.user_metadata?.role,
      },
    });

    // Check if any user role matches any required role
    const hasAccess = requiredRoles.some(required =>
      userRoles.some(userRole =>
        userRole.toLowerCase() === required.toLowerCase()
      )
    );

    if (import.meta.env.DEV) console.log('[useHasRole] Access decision:', hasAccess);
    return hasAccess;
  }, [profile, orgUser, requiredRoles]);
}

/**
 * Check if user is a VeraScore admin (internal staff)
 */
export function useIsVeraScoreAdmin(): boolean {
  return useHasRole(['verascore_admin', 'admin']);
}

/**
 * Check if user is an auditor/regulator
 */
export function useIsAuditor(): boolean {
  return useHasRole(['auditor']);
}

/**
 * Check if user can access Privacy KPI Dashboard
 * Requires BOTH:
 * 1. Feature flag enabled (VITE_FEATURE_PRIVACY_KPI=true)
 * 2. User has admin role OR is in temp admin list
 */
export function useCanAccessPrivacyKpis(): boolean {
  const hasRole = useHasRole([...ADMIN_ROLES]);

  // Feature flag must be enabled
  if (!FEATURE_PRIVACY_KPI_ENABLED) {
    if (import.meta.env.DEV) console.log('[useCanAccessPrivacyKpis] Feature flag disabled');
    return false;
  }

  return hasRole;
}

export default useHasRole;

